import type { ParentSharedStudent } from '@/types';
import { daysBetween, formatDate, formatElapsedDays } from '@/lib/datetime';
import { dayTimeLabels, listDayTimes } from '@/lib/schedule';

interface ParentStudentCardProps {
  student: ParentSharedStudent;
}

export default function ParentStudentCard({ student }: ParentStudentCardProps) {
  const initial = (student.name || '?').trim().charAt(0).toUpperCase();
  const elapsed = formatElapsedDays(daysBetween(student.first_lesson_date));
  const days = student.class_days ?? [];
  const timeLabels = dayTimeLabels(student, days);

  return (
    <section className="rounded-2xl border border-background-200 bg-gradient-to-br from-primary-50 to-background-100 p-5 md:p-7">
      <div className="flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-500 text-xl font-bold text-background-50">
          {initial}
        </span>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-foreground-950 md:text-2xl">{student.name}</h1>
          <p className="mt-0.5 text-sm text-foreground-600">
            {student.school || '학교 미입력'} · {student.grade || '학년 미입력'}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {student.target_school && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-flag-2-line"></i>
                목표 · {student.target_school}
              </span>
            )}
            <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-700">
              <i className="ri-graduation-cap-line"></i>
              {student.target_type || '고등학교'}
            </span>
          </div>

          {(student.desired_major || student.desired_career) && (
            <div className="mt-3 flex flex-wrap gap-2">
              {student.desired_major && (
                <span className="flex items-center gap-1.5 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-1.5 text-xs">
                  <i className="ri-award-line text-secondary-700"></i>
                  <span className="text-foreground-500">희망 학과</span>
                  <span className="font-semibold text-foreground-800">{student.desired_major}</span>
                </span>
              )}
              {student.desired_career && (
                <span className="flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs">
                  <i className="ri-compass-3-line text-primary-700"></i>
                  <span className="text-foreground-500">희망 진로</span>
                  <span className="font-semibold text-foreground-800">{student.desired_career}</span>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-1.5">
        {days.length === 0 ? (
          <span className="rounded-md bg-background-100 px-2 py-1 text-xs text-foreground-500">
            수업 요일 미입력
          </span>
        ) : (
          days.map((d) => (
            <span
              key={d}
              className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-xs font-semibold text-primary-700"
            >
              {d}
            </span>
          ))
        )}
        {timeLabels.length > 0 && (
          <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs font-medium text-foreground-700">
            <i className="ri-time-line"></i>
            {timeLabels.join(' · ')}
          </span>
        )}
      </div>

      {student.lesson_location && (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5 text-xs">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-map-pin-2-line"></i>
          </span>
          <span className="text-foreground-500">교습 장소</span>
          <span className="font-semibold text-foreground-800">{student.lesson_location}</span>
        </div>
      )}

      {student.goal_progress && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-accent-200 bg-accent-50 p-4">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-flag-2-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-accent-800">목표 진도</p>
            <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-800">
              {student.goal_progress}
            </p>
            {student.goal_deadline && (
              <p className="mt-1 text-xs text-foreground-500">
                목표 달성일 · {formatDate(student.goal_deadline)}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-background-200 bg-background-50 p-4">
          <p className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-seedling-line"></i>
            함께한 기간
          </p>
          <p className="mt-1 text-lg font-bold text-primary-700">{elapsed}</p>
        </div>
        <div className="rounded-xl border border-background-200 bg-background-50 p-4">
          <p className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-calendar-check-line"></i>
            첫 수업일
          </p>
          <p className="mt-1 text-base font-bold text-foreground-900">
            {formatDate(student.first_lesson_date)}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 border-t border-background-300/60 pt-4 text-xs text-foreground-600">
        <span>
          <span className="font-semibold text-foreground-800">개념 </span>
          {student.concept_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">연산 </span>
          {student.calc_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">유형 </span>
          {student.type_book || '-'}
        </span>
        <span>
          <span className="font-semibold text-foreground-800">심화 </span>
          {student.advanced_book || '-'}
        </span>
        {student.completed_books && (
          <span className="flex items-start gap-1">
            <i className="ri-checkbox-circle-line mt-0.5 text-primary-600"></i>
            <span>
              <span className="font-semibold text-foreground-800">한번 다 푼 교재 </span>
              <span className="whitespace-pre-wrap">{student.completed_books}</span>
            </span>
          </span>
        )}
      </div>
    </section>
  );
}